import { Node } from "@xyflow/react"
import { Sheet, SheetContent } from "design-system/atoms/Sheet"
import { AnimatePresence, motion } from "framer-motion"
import { useTrackEvent } from "lib/mixpanel"
import { ensure, TRACKING_EVENTS } from "lopermut"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { useWorkflowBuilder } from "../hooks/useWorkflowBuilder"
import { ActionSheet } from "./ActionSheet"
import { ConditionSheet } from "./ConditionSheet"
import { EventSheet } from "./EventSheet"

const SHEET_TYPES = {
  event: EventSheet,
  condition: ConditionSheet,
  action: ActionSheet,
}

function getSheetWidth(_type: keyof typeof SHEET_TYPES) {
  return 480
}

export function CampaignSheet() {
  const trackEvent = useTrackEvent()

  const { focusedNode, setFocusedNode } = useWorkflowBuilder()

  const flowContainerRef = useRef<HTMLDivElement | null>(null)
  const sheetRef = useRef<HTMLDivElement | null>(null)

  const setFlowContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      const flowContainer = node.closest(".react-flow")
      if (flowContainer instanceof HTMLDivElement) {
        flowContainerRef.current = flowContainer
      }
    }
  }, [])

  const previousFocusedNode = useRef<Node | null>(null)
  const SheetComponent = useMemo(() => {
    if (!focusedNode) {
      return SHEET_TYPES[previousFocusedNode.current?.type as keyof typeof SHEET_TYPES] ?? null
    }

    previousFocusedNode.current = focusedNode
    return ensure(SHEET_TYPES[focusedNode.type as keyof typeof SHEET_TYPES])
  }, [focusedNode])

  useEffect(() => {
    if (focusedNode) {
      trackEvent(TRACKING_EVENTS.CAMPAIGN_SIDEPANEL_OPENED, {
        campaignId: focusedNode.id,
        campaignType: focusedNode.type,
      })
    }
  }, [focusedNode, trackEvent])

  return (
    <div ref={setFlowContainerRef}>
      <Sheet
        open={!!focusedNode}
        onSheetOpenChange={(open) => {
          if (!open) setFocusedNode(null)
        }}
        modal={false}
      >
        <SheetContent
          side="left"
          container={flowContainerRef.current}
          // Prevent the sheet from closing when the user clicks outside of it
          onPointerDownOutside={(e) => e.preventDefault()}
          className="transition-[width] duration-300 ease-in-out max-w-[calc(100%-1rem)] sm:max-w-[calc(100%-1rem)] rounded-lg border h-auto my-2 data-[state=open]:mx-2"
          style={{ width: getSheetWidth(focusedNode?.type as keyof typeof SHEET_TYPES) }}
          ref={sheetRef}
        >
          <AnimatePresence mode="popLayout">
            <motion.div
              key={focusedNode?.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {SheetComponent && <SheetComponent />}
            </motion.div>
          </AnimatePresence>
        </SheetContent>
      </Sheet>
    </div>
  )
}
